import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { listSettlements, type Settlement } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { Banknote, Download, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Une ligne = 1 virement bancaire réel (agrégé depuis N W2B wallets faits
// le même jour par la Finance Moko). Cf conversation Henock 2026-09-02 :
// Finance draine plusieurs wallets (Cartes + MM) avant d'envoyer 1 seul wire
// consolidé à TMB. Aggregation quotidienne = 1 wire/jour typique.
type DailyBankTransfer = {
  key: string;
  date: string; // premier executed_at du jour (ISO)
  currency: string;
  amountMinorTotal: number;
  movementsCount: number;
  status: Settlement["status"];
  merchantId: string;
  merchantBusinessName?: string | null;
  merchantCode?: string | null;
};

function groupByDay(items: Settlement[]): DailyBankTransfer[] {
  const buckets = new Map<string, DailyBankTransfer>();
  for (const s of items) {
    const day = s.executed_at.slice(0, 10); // YYYY-MM-DD
    const key = `${s.merchant_id}|${day}|${s.currency}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.amountMinorTotal += s.amount_minor;
      existing.movementsCount += 1;
      if (s.executed_at < existing.date) existing.date = s.executed_at;
    } else {
      buckets.set(key, {
        key,
        date: s.executed_at,
        currency: s.currency,
        amountMinorTotal: s.amount_minor,
        movementsCount: 1,
        status: s.status,
        merchantId: s.merchant_id,
        merchantBusinessName: s.merchant_business_name,
        merchantCode: s.merchant_code,
      });
    }
  }
  return Array.from(buckets.values()).sort((a, b) => b.date.localeCompare(a.date));
}

/** Fetch toutes les pages de settlements en itérant sur next_cursor. */
async function fetchAllSettlements(): Promise<Settlement[]> {
  const all: Settlement[] = [];
  let cursor: string | undefined;
  // Garde-fou anti-boucle infinie (10k rows max)
  for (let i = 0; i < 100; i++) {
    const r = await listSettlements({ cursor, limit: 100 });
    all.push(...r.data);
    if (!r.has_more || !r.next_cursor) break;
    cursor = r.next_cursor;
  }
  return all;
}

export function SettlementsPage() {
  const auth = useAuth();
  const isMokoStaff = auth.status === "authenticated" && !auth.user.merchant_id;
  const [items, setItems] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    listSettlements()
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const daily = useMemo(() => groupByDay(items), [items]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const all = await fetchAllSettlements();
      const dailyAll = groupByDay(all);

      // Onglet 1 : agrégé par jour (ce que le marchand voit)
      const aggregated = dailyAll.map((d) => ({
        Date: d.date.slice(0, 10),
        Type: "Bank Transfer",
        Banque: "TMB - Trust Merchant Bank S.A.R.L.",
        Devise: d.currency,
        "Montant → banque": d.amountMinorTotal / 100,
        Statut: d.status,
        "Mouvements wallets": d.movementsCount,
        Marchand: d.merchantBusinessName ?? "",
        "Code marchand": d.merchantCode ?? "",
      }));

      // Onglet 2 : détail brut par W2B individuel (audit trail)
      const detail = all.map((s) => ({
        Date: s.executed_at,
        Type: "Bank Transfer",
        Banque: "TMB - Trust Merchant Bank S.A.R.L.",
        "Wallet source": s.wallet_type,
        "Réf W2B": s.reference,
        "Payment ID": s.id,
        Devise: s.currency,
        Montant: s.amount_minor / 100,
        Statut: s.status,
        Description: s.description ?? "",
        Marchand: s.merchant_business_name ?? "",
        "Code marchand": s.merchant_code ?? "",
      }));

      const wb = XLSX.utils.book_new();
      const wsAgg = XLSX.utils.json_to_sheet(aggregated);
      XLSX.utils.book_append_sheet(wb, wsAgg, "Virements TMB (agrégés)");
      const wsDetail = XLSX.utils.json_to_sheet(detail);
      XLSX.utils.book_append_sheet(wb, wsDetail, "Détail W2B");

      const today = new Date().toISOString().slice(0, 10);
      // Nom marchand depuis la 1ère row si dispo, sinon "aviation" (Moko staff cross-tenant)
      const merchant = dailyAll[0]?.merchantBusinessName || "aviation";
      const safe = merchant.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
      XLSX.writeFile(wb, `settlements-${safe}-${today}.xlsx`);
    } catch (e) {
      console.error("export failed", e);
      alert("Erreur lors de l'export. Réessaie ou contacte le support.");
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnDef<DailyBankTransfer>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 130,
      sortable: true,
      accessor: (d) => d.date,
      cell: (d) => <span className="text-sm font-semibold text-[#181c32]">{formatDate(d.date).split(" ")[0]}</span>,
    },
    ...(isMokoStaff
      ? ([
          {
            key: "merchant_name",
            header: "Marchand",
            minWidth: 180,
            cell: (d) => (
              <span className="text-sm text-[#181c32]">{d.merchantBusinessName ?? "—"}</span>
            ),
          },
          {
            key: "merchant_code",
            header: "Code marchand",
            minWidth: 150,
            cell: (d) => (
              <span className="font-mono text-xs text-[#7e8299]">{d.merchantCode ?? "—"}</span>
            ),
          },
        ] as ColumnDef<DailyBankTransfer>[])
      : []),
    {
      key: "type",
      header: "Type",
      minWidth: 130,
      cell: () => (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 text-brand-700 px-2.5 py-1 text-xs font-semibold">
          Bank Transfer
        </span>
      ),
    },
    {
      key: "bank",
      header: "Banque",
      minWidth: 240,
      cell: () => (
        <span className="text-sm text-[#181c32]">TMB - Trust Merchant Bank S.A.R.L.</span>
      ),
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 70,
      cell: (d) => <span className="font-semibold">{d.currency}</span>,
    },
    {
      key: "amount",
      header: "Montant → banque",
      align: "right",
      minWidth: 160,
      cell: (d) => (
        <span className="font-bold text-success-700">{formatAmount(d.amountMinorTotal, d.currency)}</span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 100,
      cell: (d) => <Badge color={statusColor(d.status)}>{d.status}</Badge>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Settlements"
        breadcrumbs={[{ label: "Finance" }, { label: "Settlements" }]}
      />

      {items.length === 0 && !loading ? (
        <EmptyState />
      ) : (
        <>
          <ModelExplainer />
          <div className="flex justify-end mb-3">
            <button
              onClick={handleExport}
              disabled={exporting || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 disabled:bg-brand-300 disabled:cursor-not-allowed text-white px-4 py-2 text-sm font-semibold transition-colors"
            >
              <Download size={16} />
              {exporting ? "Export en cours..." : "Exporter tout (Excel)"}
            </button>
          </div>
          <DataTable<DailyBankTransfer>
            title="Historique des virements vers la banque"
            columns={columns}
            data={daily}
            rowKey={(d) => d.key}
            loading={loading}
            initialSort={{ key: "date", dir: "desc" }}
          />
        </>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card">
      <div className="card-body py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-50 text-brand-700 mb-4">
          <Banknote size={28} />
        </div>
        <div className="text-lg font-bold text-[#181c32] mb-2">Aucun virement vers la banque pour le moment</div>
        <div className="text-sm text-[#7e8299] max-w-md mx-auto leading-relaxed">
          Les virements TMB apparaissent ici dès que la Finance Moko exécute un Wallet-to-Bank
          pour votre compte. Une ligne = 1 wire quotidien consolidé.
        </div>
      </div>
    </div>
  );
}

function ModelExplainer() {
  return (
    <div className="rounded-xl bg-brand-50 border border-brand-100 p-4 mb-4 flex gap-3">
      <Info size={18} className="text-brand-700 flex-shrink-0 mt-0.5" />
      <div className="text-xs text-[#3f4254] leading-relaxed">
        <div className="font-semibold text-brand-800 mb-1">Comment lire cette page ?</div>
        Chaque ligne représente un <span className="font-semibold">Bank Transfer</span> vers votre
        compte TMB, agrégé sur la journée.
      </div>
    </div>
  );
}
